import { useState, useEffect } from "react";
import { Flame, ArrowRight, Zap, ShieldCheck, Truck, Timer } from "lucide-react";

const BF_START = new Date("2026-11-01T00:00:00+02:00");
const BF_END = new Date("2026-11-30T23:59:59+02:00");
export const isBlackFriday = () => { const n = new Date(); return n >= BF_START && n <= BF_END; };

export const DISPLAY = "font-['Russo_One','Exo_2'] [font-synthesis:none] uppercase tracking-normal";

export const LIFESTYLE_IMAGES = [
  { url: "https://images.unsplash.com/photo-1627489107638-2d42cf60bc1b?w=800&q=80", caption: "Stil & libertate" },
  { url: "https://images.unsplash.com/photo-1743050257598-893dc90ed340?w=800&q=80", caption: "City vibes" },
  { url: "https://images.unsplash.com/photo-1568226865157-37369adb98b1?w=800&q=80", caption: "Urban fashion" },
  { url: "https://images.unsplash.com/photo-1590417396467-6cca3ebbff9b?w=800&q=80", caption: "Ride in style" },
  { url: "https://images.unsplash.com/photo-1631701589245-eae7f2af8d65?w=800&q=80", caption: "Be unique" },
];

const SLOGANS = [
  "Profitați de reduceri – stoc foarte limitat!",
  "Black Friday Scutersh – reduceri la toate categoriile",
  "Stoc foarte limitat – livrare în toată România",
  "Scutere verificate mecanic – prețuri de Black Friday",
];

const calcLeft = () => {
  const d = Math.max(0, BF_END - new Date());
  return [Math.floor(d / 86400000), Math.floor((d % 86400000) / 3600000), Math.floor((d % 3600000) / 60000), Math.floor((d % 60000) / 1000)];
};

function Countdown() {
  const [left, setLeft] = useState(calcLeft);
  useEffect(() => { const id = setInterval(() => setLeft(calcLeft()), 1000); return () => clearInterval(id); }, []);
  const labels = ["Zile", "Ore", "Min", "Sec"];
  return (
    <div data-testid="bf-countdown" className="flex gap-2">
      {left.map((v, i) => (
        <div key={labels[i]} className="text-center">
          <div className="font-['Russo_One','Exo_2'] [font-synthesis:none] font-bold text-xl sm:text-3xl bg-[#13131A] border border-[#FF0033]/40 text-white rounded-lg w-12 sm:w-16 py-2 shadow-[0_0_20px_#FF00334D]">{String(v).padStart(2, "0")}</div>
          <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">{labels[i]}</span>
        </div>
      ))}
    </div>
  );
}

export function UrgencyMarquee() {
  const items = [...SLOGANS, ...SLOGANS];
  return (
    <div data-testid="bf-marquee" className="bg-[#FF0033] text-white overflow-hidden py-2">
      <div className="flex gap-10 whitespace-nowrap animate-[bfmarquee_30s_linear_infinite] w-max">
        {items.map((s, i) => (
          <span key={i} className={`${DISPLAY} font-extrabold text-sm sm:text-base flex items-center gap-2`}><Zap className="w-4 h-4 fill-white" />{s}</span>
        ))}
      </div>
      <style>{`@keyframes bfmarquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }`}</style>
    </div>
  );
}

export function BFHero({ maxDiscount, onCta, logo, children }) {
  const bf = isBlackFriday();
  return (
    <section data-testid="boutique-hero" className="relative overflow-hidden bg-[#0A0A0E]">
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#FF0033]/20 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-4 py-8 sm:py-14 grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="flex items-center gap-3 mb-5">
            <img src={logo} alt="Scutersh" className="w-11 h-11 rounded-lg object-contain" />
            <span className={`${DISPLAY} font-black text-2xl text-white`}>Scutersh<span className="text-[#FF0033]">.store</span></span>
          </div>
          {bf && (
            <div data-testid="bf-hero-badge" className="inline-flex items-center gap-2 bg-[#FF0033] text-white px-3 py-1 rounded-sm mb-4 shadow-[0_0_24px_#FF00334D]">
              <Flame className="w-4 h-4" /><span className={`${DISPLAY} font-extrabold`}>Black Friday {maxDiscount > 0 ? `· până la -${maxDiscount}%` : ""}</span>
            </div>
          )}
          <h1 className={`${DISPLAY} text-4xl sm:text-6xl lg:text-7xl leading-[0.95] text-white`}>
            {bf ? <>Profitați de <span className="text-[#FF0033]">reduceri</span></> : <>Scutere <span className="text-[#FF0033]">verificate</span></>}
          </h1>
          <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-md">
            {bf ? <><strong className="text-[#FFD700]">Stoc foarte limitat!</strong> Scutere second hand verificate mecanic și piese, la prețuri de Black Friday.</> : "Scutere second hand verificate mecanic și piese, livrare în toată România."}
          </p>
          {bf && <div className="mt-6"><p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5"><Timer className="w-3.5 h-3.5" />Oferta expiră în</p><Countdown /></div>}
          <div className="mt-6 max-w-xl">{children}</div>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <button data-testid="hero-cta" onClick={onCta} className={`${DISPLAY} font-extrabold text-lg bg-[#FF0033] hover:bg-[#e0002d] text-white px-6 py-3 rounded-sm flex items-center gap-2 transition-transform hover:-translate-y-0.5 shadow-[0_0_30px_#FF00334D]`}>
              {bf ? "Vezi reducerile" : "Vezi scuterele"}<ArrowRight className="w-5 h-5" />
            </button>
            <span className="text-xs text-slate-400 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-400" />Verificare mecanică</span>
            <span className="text-xs text-slate-400 flex items-center gap-1.5"><Truck className="w-4 h-4 text-[#FFD700]" />Livrare în România</span>
          </div>
          <div className="sm:hidden mt-6 grid grid-cols-2 gap-2">
            {[LIFESTYLE_IMAGES[0], LIFESTYLE_IMAGES[1]].map(img => (
              <img key={img.url} src={img.url} alt={img.caption} className="w-full aspect-[3/4] object-cover rounded-lg" />
            ))}
          </div>
        </div>
        <div className="hidden sm:grid grid-cols-2 gap-3 h-[460px]">
          <img src={LIFESTYLE_IMAGES[0].url} alt="Scutersh lifestyle" className="w-full h-full object-cover rounded-lg row-span-2" />
          <img src={LIFESTYLE_IMAGES[4].url} alt="Scutersh lifestyle" className="w-full h-[224px] object-cover rounded-lg" />
          <div className="relative rounded-lg overflow-hidden h-[224px]">
            <img src={LIFESTYLE_IMAGES[1].url} alt="Scutersh lifestyle" className="w-full h-full object-cover" />
            {bf && <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center"><span className={`${DISPLAY} font-black text-5xl text-[#FF0033]`}>{maxDiscount > 0 ? `-${maxDiscount}%` : "Sale"}</span><span className={`${DISPLAY} text-white font-bold`}>Black Friday</span></div>}
          </div>
        </div>
      </div>
    </section>
  );
}

const CATEGORY_LABELS = {
  fara_permis_25: { title: "Fără permis", sub: "25 km/h" },
  cu_permis_50: { title: "Cu permis", sub: "50cc" },
  "125cc": { title: "Scutere", sub: "125cc" },
  mai_mare: { title: "Scutere mari", sub: "peste 125cc" },
};

export function CategoryDiscountTiles({ types, scooters, discounts, active, onSelect }) {
  return (
    <div data-testid="category-discount-tiles" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {types.map(st => {
        const pct = discounts[st.value] || 0;
        const count = scooters.filter(st.filter).length;
        const on = active === st.value;
        return (
          <button key={st.value} data-testid={`cat-tile-${st.value}`} onClick={() => onSelect(on ? "all" : st.value)}
            className={`relative text-left p-4 rounded-lg border overflow-hidden transition-[transform,border-color,background-color] duration-200 hover:-translate-y-1 ${on ? "border-[#FF0033] bg-[#FF0033]/15" : "border-[#262636] bg-[#13131A] hover:border-[#FF0033]/60"}`}>
            {pct > 0 && <span data-testid={`cat-tile-discount-${st.value}`} className={`${DISPLAY} absolute top-2 right-2 bg-[#FF0033] text-white font-black text-lg px-2 rounded-sm`}>-{pct}%</span>}
            <p className={`${DISPLAY} font-black text-xl sm:text-2xl text-white leading-none`}>{CATEGORY_LABELS[st.value].title}</p>
            <p className="text-sm text-slate-400 mt-1">{CATEGORY_LABELS[st.value].sub}</p>
            <p className="text-xs text-[#FFD700] font-semibold mt-3">{count} disponibile</p>
          </button>
        );
      })}
    </div>
  );
}

export function BFLifestyle() {
  return (
    <section data-testid="bf-lifestyle" className="bg-[#0A0A0E] py-10">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-end justify-between mb-4">
          <h2 className={`${DISPLAY} font-black text-3xl sm:text-4xl text-white`}>Ride in <span className="text-[#FF0033]">style</span></h2>
          <span className="text-xs text-slate-400 hidden sm:block">Black Friday Lifestyle</span>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
          {LIFESTYLE_IMAGES.map(img => (
            <div key={img.url} className="snap-start flex-shrink-0 w-52 sm:w-64 relative rounded-lg overflow-hidden group">
              <img src={img.url} alt={img.caption} loading="lazy" className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent" />
              <div className="absolute bottom-3 left-3">
                {isBlackFriday() && <span className="bg-[#FF0033] text-white text-[10px] font-black px-2 py-0.5 rounded-sm">BLACK FRIDAY</span>}
                <p className={`${DISPLAY} text-white font-extrabold text-xl mt-1`}>{img.caption}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
